<template>
  <div class="context-menu bg-white dark:bg-slate-700 shadow-xl rounded-lg">
    <menu-item
      v-if="!hasUnreadMessages"
      :option="unreadOption"
      variant="icon"
      @click="$emit('mark-as-unread')"
    />
    <menu-item
      :option="pinnedConfig"
      variant="icon"
      @click="$emit('pin', conversationId)"
    />
    <menu-item-with-submenu
      v-if="hasInboxPermission('Inbox move to pending', inboxId) && !isSnoozed"
      :option="snoozeOption"
      variant="image"
    >
      <menu-item
        v-for="(option, i) in snoozeOption.options"
        :key="i"
        :option="option"
        @click="snoozeConversation(option.key)"
      />
    </menu-item-with-submenu>
    <template v-for="option in statusMenuConfig">
      <menu-item
        v-if="show(option.key)"
        :key="option.key"
        :option="option"
        :variant="option.variant"
        @click="toggleStatus(option.key, null)"
      />
    </template>
    <menu-item-with-submenu
      v-if="hasInboxPermission('Inbox can assign', inboxId)"
      :option="agentMenuConfig"
      variant="icon"
      :sub-menu-available="!!agentsList.length"
    >
      <agent-loading-placeholder v-if="assignableAgentsUiFlags.isFetching" />
      <template v-else>
        <menu-item
          v-for="agent in agentsList"
          :key="agent.id"
          :option="generateMenuLabelConfig(agent, 'agent')"
          variant="agent"
          @click="$emit('assign-agent', agent)"
        />
      </template>
    </menu-item-with-submenu>
    <menu-item-with-submenu
      v-if="hasInboxPermission('Inbox assign to group', inboxId)"
      :option="teamMenuConfig"
      variant="icon"
      :sub-menu-available="!!teams.length"
    >
      <menu-item
        v-for="team in teams"
        :key="team.id"
        :option="generateMenuLabelConfig(team, 'team')"
        @click="$emit('assign-team', team)"
      />
    </menu-item-with-submenu>
    <menu-item-with-submenu
      v-if="hasInboxPermission('Inbox assign labels', inboxId)"
      :option="labelMenuConfig"
      variant="icon"
      :sub-menu-available="!!labels.length"
    >
      <menu-item
        v-for="label in labels"
        :key="label.id"
        :option="generateMenuLabelConfig(label, 'label')"
        variant="label"
        @click="$emit('assign-label', label)"
      />
    </menu-item-with-submenu>
    <menu-item-with-submenu
      v-if="hasInboxPermission('Inbox priority of conversations', inboxId)"
      :option="priorityConfig"
      variant="icon"
    >
      <menu-item
        v-for="(option, i) in priorityConfig.options"
        :key="i"
        :option="option"
        @click="assignPriority(option.key)"
      />
    </menu-item-with-submenu>
  </div>
</template>

<script>
import MenuItem from './menuItem.vue';
import MenuItemWithSubmenu from './menuItemWithSubmenu.vue';
import wootConstants from 'dashboard/constants/globals';
import agentMixin from 'dashboard/mixins/agentMixin';
import { mapGetters } from 'vuex';
import AgentLoadingPlaceholder from './agentLoadingPlaceholder.vue';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import conversationLabelMixin from 'dashboard/mixins/conversation/labelMixin';
import { getUnixTime, addHours, formatISO } from 'date-fns';
import snoozeIcon from 'dashboard/assets/images/snooze.svg';
import agentIcon from 'dashboard/assets/images/agent.svg';
import botIcon from 'dashboard/assets/images/bot.svg';

export default {
  components: {
    MenuItem,
    MenuItemWithSubmenu,
    AgentLoadingPlaceholder,
  },
  mixins: [agentMixin, conversationLabelMixin, hasPermissionMixin],
  props: {
    status: {
      type: String,
      default: '',
    },
    hasUnreadMessages: {
      type: Boolean,
      default: false,
    },
    inboxId: {
      type: Number,
      default: null,
    },
    priority: {
      type: String,
      default: null,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    pinned: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      STATUS_TYPE: wootConstants.STATUS_TYPE,
      unreadOption: {
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_UNREAD'),
        icon: 'mark_email_unread',
      },
      statusMenuConfig: [
        {
          key: wootConstants.STATUS_TYPE.RESOLVED,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.RESOLVED'),
          icon: 'check_circle',
          variant: 'icon',
        },
        {
          key: wootConstants.STATUS_TYPE.PENDING,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.PENDING'),
          icon: agentIcon,
          variant: 'image',
        },
        {
          key: wootConstants.STATUS_TYPE.OPEN,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.OPEN'),
          icon: botIcon,
          variant: 'image',
        },
        // d99d
        {
          key: 'reopen',
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.REOPEN'),
          icon: 'check',
          variant: 'icon',
        },
        // .
      ],
      snoozeOption: {
        key: wootConstants.STATUS_TYPE.SNOOZED,
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.TITLE'),
        icon: snoozeIcon,
        options: [
          {
            key: 1,
            label: this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.OPTIONS.HOUR'
            ),
          },
          {
            key: 2,
            label: this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.OPTIONS.HOURS',
              { hours: 2 }
            ),
          },
          {
            key: 3,
            label: this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.OPTIONS.HOURS',
              { hours: 3 }
            ),
          },
          {
            key: 24,
            label: this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.OPTIONS.HOURS',
              { hours: 24 }
            ),
          },
        ],
      },
      priorityConfig: {
        key: 'priority',
        label: this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.TITLE'),
        icon: 'personal_places',
        options: [
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
            key: null,
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
            key: 'urgent',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
            key: 'high',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
            key: 'medium',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
            key: 'low',
          },
        ].filter(item => item.key !== this.priority),
      },
      labelMenuConfig: {
        key: 'label',
        icon: 'shoppingmode',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_LABEL'),
      },
      agentMenuConfig: {
        key: 'agent',
        icon: 'person',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_AGENT'),
      },
      teamMenuConfig: {
        key: 'team',
        icon: 'groups',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_TEAM'),
      },
      pinnedConfig: {
        key: 'pinned',
        icon: 'pin',
        label: this.pinned
          ? this.$t('CONVERSATION.CARD_CONTEXT_MENU.UNPIN')
          : this.$t('CONVERSATION.CARD_CONTEXT_MENU.PIN'),
      },
    };
  },
  computed: {
    ...mapGetters({
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      assignableAgentsUiFlags: 'inboxAssignableAgents/getUIFlags',
    }),
    isSnoozed() {
      return this.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
  },

  mounted() {
    this.$store.dispatch('inboxAssignableAgents/fetch', [this.inboxId]);
  },
  methods: {
    toggleStatus(status, snoozedUntil) {
      this.$emit(
        'update-conversation',
        status === 'reopen' ? 'open' : status,
        snoozedUntil
      );
    },
    snoozeConversation(hour) {
      this.toggleStatus(
        wootConstants.STATUS_TYPE.SNOOZED,
        formatISO(addHours(new Date(), hour))
      );
    },
    assignPriority(priority) {
      this.$emit('assign-priority', priority);
    },
    show(key) {
      // d99d
      const hasResolvePermission = this.hasInboxPermission(
        'Inbox resolve',
        this.inboxId
      );
      const hasSwitchPermission = this.hasInboxPermission(
        'Inbox manual/bot switch',
        this.inboxId
      );
      const hasReopenPermission = this.hasInboxPermission(
        'Inbox reopen',
        this.inboxId
      );
      switch (key) {
        case wootConstants.STATUS_TYPE.RESOLVED:
          return (
            this.status !== wootConstants.STATUS_TYPE.RESOLVED &&
            hasResolvePermission
          );
          break;
        case wootConstants.STATUS_TYPE.OPEN:
          return (
            this.status === wootConstants.STATUS_TYPE.PENDING &&
            hasSwitchPermission
          );
          break;
        case wootConstants.STATUS_TYPE.PENDING:
          return (
            this.status === wootConstants.STATUS_TYPE.OPEN &&
            hasSwitchPermission
          );
          break;
        case 'reopen':
          return (
            [
              wootConstants.STATUS_TYPE.RESOLVED,
              wootConstants.STATUS_TYPE.SNOOZED,
            ].includes(this.status) && hasReopenPermission
          );
          break;
        default:
          return false;
      }
      //
      // If the conversation status is same as the action, then don't display the option
      // i.e.: Don't show an option to resolve if the conversation is already resolved.
      return this.status !== key;
    },
    generateMenuLabelConfig(option, type = 'text') {
      return {
        key: option.id,
        ...(type === 'icon' && { icon: option.icon }),
        ...(type === 'label' && { color: option.color }),
        ...(type === 'agent' && { thumbnail: option.thumbnail }),
        ...(type === 'agent' && { status: option.availability_status }),
        ...(type === 'text' && { label: option.label }),
        ...(type === 'label' && {
          label: option.title,
          active: this.savedLabels.includes(option.title),
        }),
        ...(type === 'agent' && { label: option.name }),
        ...(type === 'team' && { label: option.name }),
      };
    },
  },
};
</script>
<style lang="scss" scoped>
.context-menu {
  > div:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
  > div:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
}
</style>
